import { useState, useEffect } from "react";
import { onboardingAPI } from "../api/onboardingAPI";
import { safeGetItem, safeRemoveItem, safeSetItem } from "@mediatechgroup/app-version";

interface UseOnboardingOptions {
  storageKey: string;
  autoStart?: boolean;
}

/**
 * Hook to manage onboarding walkthrough state
 * @param storageKey - Unique key for localStorage (e.g., 'onboarding-dashboard-admin')
 * @param autoStart - Whether to automatically start the walkthrough if not completed
 * @returns Object with state and control functions
 */
export const useOnboarding = ({
  storageKey,
  autoStart = true,
}: UseOnboardingOptions) => {
  const [isCompleted, setIsCompleted] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const userDetails = JSON.parse(safeGetItem("userDetails") || "{}");
  const userId = userDetails?.id;

  // Fetch status from localStorage or backend API
  useEffect(() => {
    const localStatus = safeGetItem(storageKey);
    const isLocalCompleted =
      localStatus === "completed" || localStatus === "dismissed";

    if (isLocalCompleted) {
      setIsCompleted(true);
      setShouldShow(false);
      return;
    }

    // If user is logged in, fetch onboarding status from backend API
    const fetchStatus = async () => {
      if (!userId) {
        // No user logged in → rely on localStorage
        if (!isLocalCompleted && autoStart) {
          setShouldShow(true);
          setIsActive(true);
        }
        return;
      }

      try {
        const data = await onboardingAPI.getStatus(storageKey);

        if (data?.status === "completed" || data?.status === "dismissed") {
          safeSetItem(storageKey, data.status);
          setIsCompleted(true);
          setShouldShow(false);
        } else if (autoStart) {
          setShouldShow(true);
          setIsActive(true);
        } else {
          setShouldShow(true);
        }
      } catch {
        // On error, show onboarding by default (better UX)
        if (autoStart) {
          setShouldShow(true);
          setIsActive(true);
        }
      }
    };

    fetchStatus();
  }, [storageKey, userId, autoStart]);

  const saveStatus = async (status: "completed" | "dismissed") => {
    // Update local state and localStorage immediately
    safeSetItem(storageKey, status);
    setIsCompleted(true);
    setShouldShow(false);
    setIsActive(false);

    // Skip API call if not logged in
    if (!userId) {
      return;
    }

    // Save to backend API
    try {
      await onboardingAPI.saveStatus(storageKey, status);
    } catch {
      // Axios interceptor handles error reporting
    }
  };

  const markAsCompleted = () => {
    saveStatus("completed");
  };

  const markAsDismissed = () => {
    saveStatus("dismissed");
  };

  const reset = async () => {
    // Update local state and localStorage immediately
    safeRemoveItem(storageKey);
    setIsCompleted(false);
    setShouldShow(true);
    setIsActive(true);

    // Delete from backend API if user is logged in
    if (userId) {
      try {
        await onboardingAPI.deleteStatus(storageKey);
      } catch {
        // Axios interceptor handles error reporting
      }
    }
  };

  const startWalkthrough = () => {
    setShouldShow(true);
    setIsActive(true);
  };

  return {
    isCompleted,
    shouldShow,
    isActive,
    markAsCompleted,
    markAsDismissed,
    reset,
    startWalkthrough,
  };
};
